/**
 * Normalizes a phone number to E.164 format (+1XXXXXXXXXX for US numbers)
 * Handles various input formats:
 * - Already formatted: +15551234567
 * - AWS Cognito format (no +): 15551234567
 * - Without country code: 5551234567
 * - With formatting: (555) 123-4567
 * - With spaces/dashes: 555-123-4567 or 555 123 4567
 */
export function normalizePhoneToE164(phone: string): string {
  if (!phone) return phone;

  // Remove all non-digit characters except leading +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // If it already starts with +, return as is
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // If it starts with 1 and has 11 digits, add + (common AWS Cognito format)
  if (cleaned.startsWith('1') && cleaned.length === 11) {
    return `+${cleaned}`;
  }

  // If it has 10 digits (no country code), add +1
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }

  // If it has 11 digits (but doesn't start with 1), add + anyway
  if (cleaned.length === 11) {
    return `+${cleaned}`;
  }

  // If it has 12 digits and doesn't start with +, it might already have country code without +
  if (cleaned.length === 12 && !cleaned.startsWith('+')) {
    return `+${cleaned}`;
  }

  // Default: add + if not present
  return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
}

/**
 * Validates if a phone number is in valid E.164 format
 * E.164 format: starts with +, followed by 1-15 digits (international standard)
 *
 * @param phone - Phone number to validate
 * @returns true if valid, error message string if invalid
 */
export function validatePhoneE164(phone: string | undefined): true | string {
  if (!phone) return true; // Let required validation handle empty values

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Must start with + for E.164 format
  if (!cleaned.startsWith('+')) {
    return 'Phone number must start with country code (e.g., +1)';
  }

  const digitsOnly = cleaned.replace('+', '');

  // E.164 standard: must have between 1 and 15 digits
  if (digitsOnly.length < 1 || digitsOnly.length > 15) {
    return 'Phone number must contain 1-15 digits in E.164 format';
  }

  return true;
}
